"use client"

// [VV5-TEARDOWN] lp-free-shipping A/B test (DEV-9829) — delete this file at
// teardown. See the checklist in @local/tracking/src/experiments/freeShipping.
import { useEffect } from "react"
import { STAGE } from "@local/utils/src/services"
import { trackAction } from "@local/tracking/src/trackActions"
import {
  registerPostHogProperties,
  trackPostHog,
} from "@local/tracking/src/scripts/posthog"
import {
  VV5_FEATURE_FLAG,
  isValidVv5Variant,
  type FreeShippingVariant,
} from "@local/tracking/src/experiments/freeShipping"
import { getRecoveryStorage } from "@local/tracking/src/recover/getRecoveryStorage"

// Manual capture bypasses PostHog's built-in per-session dedup, so gate
// re-fires across remounts with a module-level Set (same pattern as the
// apm exposure beacon).
const firedVv5Exposure = new Set<string>()

/**
 * Signals lp-free-shipping exposure. Middleware buckets server-side and the
 * assignment arrives as a prop from the block builder; without this call the
 * experiment shows zero enrolled users (PostHog needs the
 * `$feature_flag_called` event to count exposure). Mounted for BOTH arms, but
 * only when middleware resolved a variant (i.e. on the sunbeater LP), so
 * exposure is correctly restricted to eligible traffic.
 */
export const Vv5ExposureBeacon = ({
  variant,
}: {
  variant: FreeShippingVariant
}) => {
  useEffect(() => {
    if (variant !== "control" && variant !== "test") {
      return
    }
    // Don't enroll QA sessions driven by a VALID `?vv5=` override — they'd bias
    // the experiment. Only off-prd: middleware ignores the override on
    // production (`middleware/freeShipping.ts` STAGE gate), so a prd visitor
    // carrying `?vv5=` is a real cookie-bucketed participant and MUST count.
    // A bogus value (e.g. `?vv5=xyz`) is ignored by middleware and falls
    // through to a real bucket, so those users MUST still be counted too.
    if (
      typeof window !== "undefined" &&
      STAGE !== "prd" &&
      isValidVv5Variant(new URLSearchParams(window.location.search).get("vv5"))
    ) {
      return
    }
    if (firedVv5Exposure.has(variant)) return
    firedVv5Exposure.add(variant)

    // PostHog convention: tags every subsequent event with the variant so
    // downstream conversions attribute to the experiment, not just exposure.
    registerPostHogProperties({
      [`$feature/${VV5_FEATURE_FLAG}`]: variant,
    })
    trackPostHog("$feature_flag_called", {
      $feature_flag: VV5_FEATURE_FLAG,
      $feature_flag_response: variant,
    })

    const { geo, loc } = getRecoveryStorage()
    trackAction("split_test", {
      experiment: VV5_FEATURE_FLAG,
      variant,
      region: geo?.region ?? loc?.regionName ?? null,
    })
  }, [variant])

  return null
}
